import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import { AddBox as AddIcon } from "@material-ui/icons";

/* ------------------------- Importaciones internas ------------------------- */
import { api } from "../../../../services/api";
import { REGEX_CUIT } from "../../../../core/utils/regex";
import { cancelButton, saveButton } from "../../../../core/styles/ui.constants";
import { useLoading } from "../../../../contexts/LoadingContext";
import BotonGenerico from "../../../../components/Button/Button";
import DialogFooter from "../../../../components/DialogFooter/DialogActions";

/* --------------------------------- Estilos -------------------------------- */
const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(4),
    minWidth: "1200px",
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  titleText: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  formRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  field: {
    flex: 1,
    minWidth: 0,
  },
  fieldHalf: {
    width: "49%",
    margin: "0 5px auto",
  },
  fullWidthField: {
    width: "100%",
  },
  footerButtons: {
    display: "flex",
    justifyContent: "end",
    marginTop: theme.spacing(3),
  },
  actionButton: saveButton,
  cancelButton: { ...cancelButton, marginLeft: theme.spacing(2) },
}));

const ABMProveedorModal = ({
  open,
  onClose,
  onSuccess,
  proveedor,
  tipoCliente,
  readOnly = false,
}) => {
  const classes = useStyles();
  const { show, hide } = useLoading();

  const normalizeEmails = (emails = []) =>
    emails
      .map((correo) =>
        typeof correo === "string"
          ? { idemails: null, proveedor_id: 0, email: correo.trim() }
          : {
            ...correo,
            email: (correo?.email || "").trim(),
          }
      )
      .filter((correo) => correo.email !== "");

  const paises = [
    {
      nombre: "Argentina",
      provincias: [
        {
          nombre: "Buenos Aires",
          localidades: [
            "La Plata",
            "Mar del Plata",
            "Tandil",
            "Bahía Blanca",
            "Olavarría",
            "Pergamino",
            "Junín",
            "Pinamar",
            "Otro",
          ],
        },
        {
          nombre: "CABA",
          localidades: ["Ciudad Autónoma de Buenos Aires", "Otro"],
        },
        {
          nombre: "Córdoba",
          localidades: [
            "Córdoba",
            "Villa María",
            "Río Cuarto",
            "Alta Gracia",
            "San Francisco",
            "Villa Carlos Paz",
            "Jesús María",
            "Otro",
          ],
        },
        {
          nombre: "Santa Fe",
          localidades: [
            "Rosario",
            "Santa Fe",
            "Rafaela",
            "Venado Tuerto",
            "Esperanza",
            "San Lorenzo",
            "Reconquista",
            "Otro",
          ],
        },
        {
          nombre: "Mendoza",
          localidades: [
            "Mendoza",
            "San Rafael",
            "Godoy Cruz",
            "San Martín",
            "Luján de Cuyo",
            "Maipú",
            "Otro",
          ],
        },
        {
          nombre: "Tucumán",
          localidades: [
            "San Miguel de Tucumán",
            "Tafí Viejo",
            "Concepción",
            "Famaillá",
            "Monteros",
            "Otro",
          ],
        },
        {
          nombre: "Salta",
          localidades: [
            "Salta",
            "Rosario de la Frontera",
            "San Ramón de la Nueva Orán",
            "Metán",
            "Cafayate",
            "Otro",
          ],
        },
        {
          nombre: "Chaco",
          localidades: [
            "Resistencia",
            "Barranqueras",
            "Sáenz Peña",
            "Charata",
            "Villa Ángela",
            "Otro",
          ],
        },
        {
          nombre: "Santiago del Estero",
          localidades: [
            "Santiago del Estero",
            "La Banda",
            "Termas de Río Hondo",
            "Frías",
            "Otro",
          ],
        },
        {
          nombre: "Misiones",
          localidades: [
            "Posadas",
            "Eldorado",
            "Oberá",
            "San Vicente",
            "Apóstoles",
            "Otro",
          ],
        },
        {
          nombre: "Catamarca",
          localidades: [
            "San Fernando del Valle de Catamarca",
            "Santa María",
            "Belén",
            "Antofagasta de la Sierra",
            "Otro",
          ],
        },
        {
          nombre: "Jujuy",
          localidades: [
            "San Salvador de Jujuy",
            "Palpalá",
            "Perico",
            "Ledesma",
            "La Quiaca",
            "Otro",
          ],
        },
        {
          nombre: "Neuquén",
          localidades: [
            "Neuquén",
            "San Martín de los Andes",
            "Zapala",
            "Cutral Có",
            "Rincón de los Sauces",
            "Otro",
          ],
        },
        {
          nombre: "Río Negro",
          localidades: [
            "Viedma",
            "Cipolletti",
            "General Roca",
            "Allen",
            "Bariloche",
            "Otro",
          ],
        },
        {
          nombre: "Chubut",
          localidades: [
            "Rawson",
            "Trelew",
            "Comodoro Rivadavia",
            "Puerto Madryn",
            "Esquel",
            "Otro",
          ],
        },
        {
          nombre: "Santa Cruz",
          localidades: ["Río Gallegos", "Calafate", "El Chaltén", "Otro"],
        },
        {
          nombre: "La Pampa",
          localidades: [
            "Santa Rosa",
            "General Pico",
            "Toay",
            "Realicó",
            "Otro",
          ],
        },
        {
          nombre: "Entre Ríos",
          localidades: [
            "Paraná",
            "Concordia",
            "Gualeguaychú",
            "La Paz",
            "Victoria",
            "Otro",
          ],
        },
        {
          nombre: "San Juan",
          localidades: [
            "San Juan",
            "Rivadavia",
            "Rawson",
            "Chimbas",
            "Albardón",
            "Otro",
          ],
        },
        {
          nombre: "San Luis",
          localidades: [
            "San Luis",
            "Villa Mercedes",
            "Merlo",
            "Justo Daract",
            "Otro",
          ],
        },
        {
          nombre: "La Rioja",
          localidades: [
            "La Rioja",
            "Chamical",
            "Chilecito",
            "Aimogasta",
            "Famatina",
            "Otro",
          ],
        },
        {
          nombre: "Formosa",
          localidades: [
            "Formosa",
            "Pirané",
            "Clorinda",
            "Las Lomitas",
            "Tatané",
            "Otro",
          ],
        },
        {
          nombre: "Corrientes",
          localidades: [
            "Corrientes",
            "Goya",
            "Mercedes",
            "Bella Vista",
            "Otro",
          ],
        },
        {
          nombre: "Tierra del Fuego",
          localidades: ["Ushuaia", "Río Grande", "Tolhuin", "Otro"],
        },
      ],
    },
    {
      nombre: "Chile",
      provincias: [
        {
          nombre: "Región Metropolitana",
          localidades: ["Santiago", "Puente Alto", "Maipú", "Otro"],
        },
      ],
    },
  ];

  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const condicionesIvaArgentina = [
    "IVA Responsable Inscripto",
    "IVA Responsable No Inscripto",
    "IVA No Responsable",
    "IVA Sujeto Exento",
    "Consumidor Final",
    "Responsable Monotributo",
    "Sujeto No Categorizado",
    "Proveedor del Exterior",
    "Cliente del Exterior",
    "IVA Liberado - Ley N° 19.640",
    "IVA Responsable Inscripto - Agente de Percepción",
    "Pequeño Contribuyente Eventual",
    "Monotributista Social",
    "Pequeño Contribuyente Eventual Social",
  ];

  const initialState = {
    estado: "",
    rol: "",
    nombres: "",
    telefono: "",
    email: [""],
    direccion: "",
    fechaRegistro: new Date().toISOString().split("T")[0],
    cuit: "",
    codigoPostal: "",
    pais: "",
    localidad: "",
    provincia: "",
    otraLocalidad: "",
    condicionFrenteAlIva: "",
    personaDeContacto: "",
  };

  const [state, setState] = useState(initialState);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (proveedor) {
        const paisProveedor = proveedor.pais || "";
        const provinciaProveedor = proveedor.provincia || "";
        const localidadProveedor = proveedor.localidad || "";
        const paisEncontrado = paises.find((p) => p.nombre === paisProveedor);
        const provinciasDelPais = paisEncontrado ? paisEncontrado.provincias : [];
        const provinciaExiste = provinciasDelPais.some(
          (prov) => prov.nombre === provinciaProveedor
        );
        const provinciaSeleccionada = provinciaExiste ? provinciaProveedor : "";
        const provinciaSeleccionadaObj = provinciasDelPais.find(
          (prov) => prov.nombre === provinciaSeleccionada
        );
        const localidadesDeProvincia = provinciaSeleccionadaObj
          ? provinciaSeleccionadaObj.localidades
          : [];
        const localidadExiste = localidadesDeProvincia.some(
          (loc) => loc === localidadProveedor
        );
        const localidadSeleccionada = localidadExiste
          ? localidadProveedor
          : localidadProveedor
            ? "Otro"
            : "";

        setProvincias(provinciasDelPais);
        setLocalidades(localidadesDeProvincia);

        setState({
          estado: proveedor.Estado || "",
          rol: proveedor.Rol || "",
          nombres: proveedor.ApellidoyNombre || "",
          telefono: proveedor.Telefono || "",
          email: proveedor.email || [],
          direccion: proveedor.Direccion || "",
          cuit: proveedor.cuit || "",
          codigoPostal: proveedor.codigoPostal || "",
          pais: paisProveedor,
          localidad: localidadSeleccionada,
          provincia: provinciaSeleccionada,
          otraLocalidad: localidadExiste ? "" : localidadProveedor,
          condicionFrenteAlIva: proveedor.condicionFrenteAlIva || "",
          personaDeContacto: proveedor.personaDeContacto || "",
          fechaRegistro: proveedor.fechaRegistro || "",
        });
      } else {
        setState(initialState);
        setProvincias([]);
        setLocalidades([]);

        if (tipoCliente) {
          handleChange("rol", tipoCliente);
          handleChange("estado", "activo");
        }
      }
    } else {
      setState(initialState);
      setErrors({});
      setProvincias([]);
      setLocalidades([]);
    }
  }, [open, proveedor]);

  const handleChange = (field, value) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleEmailChange = (index, nuevoEmail) => {
    const nuevosEmails = [...state.email];
    nuevosEmails[index] = {
      ...nuevosEmails[index], // mantenemos idemails y proveedor_id
      email: nuevoEmail,
    };
    setState((prev) => ({ ...prev, email: nuevosEmails }));
  };

  const handleAddEmail = () => {
    setState((prev) => ({
      ...prev,
      email: [...prev.email, { idemails: null, proveedor_id: 0, email: "" }],
    }));
  };

  const handleRemoveEmail = (index) => {
    const nuevosEmails = state.email.filter((_, i) => i !== index);
    setState((prev) => ({ ...prev, email: nuevosEmails }));
  };

  const handlePaisChange = (e) => {
    const paisSeleccionado = e.target.value;
    const pais = paises.find(
      (p) => p.nombre.toLowerCase() === paisSeleccionado.toLowerCase()
    );

    const provinciasDelPais = pais ? pais.provincias : [];

    setState((prev) => ({
      ...prev,
      pais: paisSeleccionado,
      provincia: "",
      localidad: "",
      otraLocalidad: "",
    }));

    setProvincias(provinciasDelPais); // Actualiza las provincias disponibles
    setLocalidades([]); // Reinicia las localidades disponibles
  };

  const handleProvinciaChange = (provinciaSeleccionada) => {
    const provincia = provincias.find(
      (p) => p.nombre === provinciaSeleccionada
    );
    const localidadesDeProvincia = provincia ? provincia.localidades : [];

    setState((prev) => ({
      ...prev,
      provincia: provinciaSeleccionada,
      localidad: "", // Reinicia la localidad al cambiar de provincia
      otraLocalidad: "",
    }));

    setLocalidades(localidadesDeProvincia); // Actualiza las localidades disponibles
  };

  const handleLocalidadChange = (localidadSeleccionada) => {
    if (localidadSeleccionada === "Otro") {
      setState((prev) => ({
        ...prev,
        localidad: "Otro",
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      localidad: localidadSeleccionada,
      otraLocalidad: "",
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!state.nombres) newErrors.nombres = "La razón social es obligatorio.";
    if (!state.cuit) newErrors.cuit = "El CUIT es obligatorio.";

    if (!state.condicionFrenteAlIva)
      newErrors.condicionFrenteAlIva =
        "La condición frente al IVA es obligatoria.";
    if (!state.rol) newErrors.rol = "El rol es obligatorio.";

    if (!state.estado) newErrors.estado = "El estado es obligatorio.";
    // if (!state.personaDeContacto)
    //   newErrors.personaDeContacto = "La persona de contacto es obligatoria.";

    // if (!state.telefono || !/^\d+$/.test(state.telefono)) {
    //   newErrors.telefono = "El teléfono es obligatorio y debe ser numérico.";
    // } else if (state.telefono.length > 15) {
    //   newErrors.telefono = "El teléfono no debe superar los 15 dígitos.";
    // }

    // if (!state.email.some((correo) => (correo.email || "").trim() !== "")) {
    //   newErrors.email = "Al menos un correo es requerido.";
    // } else {
    //   const emailInvalido = state.email.find(
    //     (correo) => !/\S+@\S+\.\S+/.test(correo.email || "")
    //   );
    //   if (emailInvalido) {
    //     newErrors.email = `El correo "${emailInvalido.email}" es inválido.`;
    //   }
    // }

    if (!state.fechaRegistro)
      newErrors.fechaRegistro = "La fecha de registro es obligatoria.";

    if (state.localidad === "Otro" && !state.otraLocalidad.trim()) {
      newErrors.otraLocalidad = "Debe indicar la localidad.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (readOnly) return;

    if (!validate()) return;

    show("Guardando proveedor...");
    setLoading(true);

    const proveedorPayload = {
      Estado: state.estado,
      Rol: state.rol,
      ApellidoyNombre: state.nombres,
      Telefono: state.telefono,
      Direccion: state.direccion,
      fechaRegistro: state.fechaRegistro,
      cuit: state.cuit,
      codigoPostal: state.codigoPostal,
      pais: state.pais,
      localidad:
        state.localidad === "Otro"
          ? state.otraLocalidad.trim()
          : state.localidad,
      provincia: state.provincia,
      condicionFrenteAlIva: state.condicionFrenteAlIva,
      personaDeContacto: state.personaDeContacto,
      oculto: false,
    };

    try {
      let idProveedor;
      let nuevoProveedor;

      if (proveedor?.idProveedor) {
        nuevoProveedor = await api.proveedores.update(
          proveedor.idProveedor,
          proveedorPayload
        );

        idProveedor = proveedor.idProveedor;

        // correos iniciales que vienen del proveedor
        const correosIniciales = proveedor.email || [];

        // correos actuales del form
        const correosActuales = state.email || [];
        const correosActualesValidos = normalizeEmails(correosActuales);

        // 1️⃣ Actualizar o crear
        await Promise.all(
          correosActualesValidos.map((correo) => {
            if (correo.idemails) {
              return api.correoProveedores.update(correo.idemails, {
                email: correo.email,
              });
            } else {
              return api.correoProveedores.create({
                proveedor_id: idProveedor,
                email: correo.email,
              });
            }
          })
        );

        // 2️⃣ Eliminar los que se sacaron
        await Promise.all(
          correosIniciales
            .filter(
              (correoInicial) =>
                !correosActualesValidos.some(
                  (correoActual) =>
                    correoActual.idemails === correoInicial.idemails
                )
            )
            .map((correoParaBorrar) =>
              api.correoProveedores.delete(correoParaBorrar.idemails)
            )
        );
      } else {
        nuevoProveedor = await api.proveedores.create(proveedorPayload);
        idProveedor = nuevoProveedor.idProveedor;
        const correosValidos = normalizeEmails(state.email);

        // 🆕 CREAR correos
        await Promise.all(
          correosValidos.map(({ email }) =>
            api.correoProveedores.create({
              proveedor_id: idProveedor,
              email: email,
            })
          )
        );
      }

      if (tipoCliente) {
        onSuccess(nuevoProveedor);
      } else {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error("Error al guardar proveedor:", err.message);
      alert(err.message || "Ocurrió un error al guardar. Intenta nuevamente.");
    } finally {
      setLoading(false);
      hide();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <DialogTitle disableTypography>
        <Typography variant="h6" className={classes.titleText}>
          Proveedores/Clientes
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className={classes.dialogContent}
        style={readOnly ? { pointerEvents: "none", opacity: 0.9 } : undefined}
      >
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Datos Fiscales
        </Typography>
        <div className={classes.formRow}>
          <div className={classes.field}>
            <Typography variant="subtitle1">Razón social <span className="text-danger">*</span></Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={state.nombres}
              onChange={(e) => handleChange("nombres", e.target.value)}
              error={!!errors.nombres || undefined}
            />
            {errors.nombres && (
              <Typography color="error" variant="body2">
                {errors.nombres}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">CUIT <span className="text-danger">*</span></Typography>
            <TextField
              variant="outlined"
              fullWidth
              type="tel"
              value={state.cuit}
              inputProps={{
                maxLength: 11,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (REGEX_CUIT.test(value)) {
                  handleChange("cuit", value);
                }
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", ".", " "].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              error={!!errors.cuit || undefined}
            />
            {errors.cuit && (
              <Typography color="error" variant="body2">
                {errors.cuit}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Condición frente al IVA <span className="text-danger">*</span></Typography>
            <Select
              variant="outlined"
              fullWidth
              value={state.condicionFrenteAlIva}
              onChange={(e) =>
                handleChange("condicionFrenteAlIva", e.target.value)
              }
              error={!!errors.condicionFrenteAlIva || undefined}
            >
              {state.condicionFrenteAlIva &&
                !condicionesIvaArgentina.includes(state.condicionFrenteAlIva) && (
                  <MenuItem value={state.condicionFrenteAlIva}>
                    {state.condicionFrenteAlIva}
                  </MenuItem>
                )}
              {condicionesIvaArgentina.map((condicion) => (
                <MenuItem key={condicion} value={condicion}>
                  {condicion}
                </MenuItem>
              ))}
            </Select>
            {errors.condicionFrenteAlIva && (
              <Typography color="error" variant="body2">
                {errors.condicionFrenteAlIva}
              </Typography>
            )}
          </div>
        </div>

        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Datos Comerciales
        </Typography>
        <div className={classes.formRow}>
          <div className={classes.field}>
            <Typography variant="subtitle1">Rol <span className="text-danger">*</span></Typography>
            <Select
              variant="outlined"
              fullWidth
              value={state.rol}
              onChange={(e) => handleChange("rol", e.target.value)}
              error={!!errors.rol || undefined}
              disabled={!!tipoCliente}
            >
              <MenuItem value="proveedor">Proveedor</MenuItem>
              <MenuItem value="cliente">Cliente</MenuItem>
            </Select>
            {errors.rol && (
              <Typography color="error" variant="body2">
                {errors.rol}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Estado <span className="text-danger">*</span></Typography>
            <Select
              variant="outlined"
              fullWidth
              value={state.estado}
              onChange={(e) => handleChange("estado", e.target.value)}
              error={!!errors.estado || undefined}
              disabled={!!tipoCliente}
            >
              <MenuItem value="activo">Activo</MenuItem>
              <MenuItem value="inactivo">Inactivo</MenuItem>
            </Select>
            {errors.estado && (
              <Typography color="error" variant="body2">
                {errors.estado}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Fecha de Registro</Typography>
            <TextField
              variant="outlined"
              type="date"
              fullWidth
              value={state.fechaRegistro}
              onChange={(e) => handleChange("fechaRegistro", e.target.value)}
              error={!!errors.fechaRegistro || undefined}
            />
            {errors.fechaRegistro && (
              <Typography color="error" variant="body2">
                {errors.fechaRegistro}
              </Typography>
            )}
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.field}>
            <Typography variant="subtitle1">Persona de contacto</Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={state.personaDeContacto}
              onChange={(e) =>
                handleChange("personaDeContacto", e.target.value)
              }
              error={!!errors.personaDeContacto || undefined}
            />
            {errors.personaDeContacto && (
              <Typography color="error" variant="body2">
                {errors.personaDeContacto}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Teléfono</Typography>
            <TextField
              variant="outlined"
              fullWidth
              type="tel"
              value={state.telefono}
              inputProps={{
                maxLength: 15,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,15}$/.test(value)) {
                  handleChange("telefono", value);
                }
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", ".", " "].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              error={!!errors.telefono || undefined}
            />
            {errors.telefono && (
              <Typography color="error" variant="body2">
                {errors.telefono}
              </Typography>
            )}
          </div>
          <div className={classes.field}></div>
        </div>

        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Ubicación
        </Typography>
        <div className={classes.formRow}>
          <div className={classes.field}>
            <Typography variant="subtitle1">País</Typography>
            <Select
              variant="outlined"
              fullWidth
              value={state.pais}
              onChange={handlePaisChange}
              error={!!errors.pais}
            >
              {paises.map((pais, index) => (
                <MenuItem key={index} value={pais.nombre}>
                  {pais.nombre}
                </MenuItem>
              ))}
            </Select>
            {errors.pais && (
              <Typography color="error" variant="body2">
                {errors.pais}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Provincia</Typography>
            <Autocomplete
              options={provincias.map((provincia) => provincia.nombre)}
              value={state.provincia || null}
              onChange={(_, value) => handleProvinciaChange(value || "")}
              disabled={!state.pais}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  error={!!errors.provincia}
                />
              )}
            />
            {errors.provincia && (
              <Typography color="error" variant="body2">
                {errors.provincia}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Localidad</Typography>
            <Autocomplete
              options={Array.from(new Set([...localidades, "Otro"]))}
              value={state.localidad || null}
              onChange={(_, value) => handleLocalidadChange(value || "")}
              disabled={!state.provincia}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  error={!!errors.localidad}
                />
              )}
            />
            {errors.localidad && (
              <Typography color="error" variant="body2">
                {errors.localidad}
              </Typography>
            )}
          </div>
        </div>

        {state.localidad === "Otro" && (
          <div className={classes.formRow}>
            <div className={classes.fieldHalf}>
              <Typography variant="subtitle1">Otra localidad</Typography>
              <TextField
                variant="outlined"
                fullWidth
                value={state.otraLocalidad}
                onChange={(e) => handleChange("otraLocalidad", e.target.value)}
                error={!!errors.otraLocalidad}
              />
              {errors.otraLocalidad && (
                <Typography color="error" variant="body2">
                  {errors.otraLocalidad}
                </Typography>
              )}
            </div>
            <div className={classes.fieldHalf}></div>
          </div>
        )}

        <div className={classes.formRow}>
          <div className={classes.field}>
            <Typography variant="subtitle1">Dirección</Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={state.direccion}
              onChange={(e) => handleChange("direccion", e.target.value)}
              error={!!errors.direccion || undefined}
            />
            {errors.direccion && (
              <Typography color="error" variant="body2">
                {errors.direccion}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Código postal</Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={state.codigoPostal}
              onChange={(e) => handleChange("codigoPostal", e.target.value)}
              error={!!errors.codigoPostal || undefined}
            />
            {errors.codigoPostal && (
              <Typography color="error" variant="body2">
                {errors.codigoPostal}
              </Typography>
            )}
          </div>
          <div className={classes.field}></div>
        </div>

        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Contacto Digital
        </Typography>
        <div className={classes.formRow}>
          <div
            className={classes.fullWidthField}
            style={{ display: "flex", flexDirection: "column" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1" style={{ marginRight: "1rem" }}>
                Correo Electrónico
              </Typography>
              <Tooltip
                title={
                  <span style={{ fontSize: "0.8rem" }}>
                    Agregar otro correo
                  </span>
                }
                arrow
              >
                <IconButton
                  color="primary"
                  onClick={handleAddEmail}
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </div>

            {state.email.map((correoObj, index) => (
              <div
                key={correoObj.idemails || index}
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <TextField
                  variant="outlined"
                  fullWidth
                  value={correoObj.email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  error={!!errors.email}
                />
                {state.email.length > 1 && (
                  <BotonGenerico
                    tipo="sinIconos"
                    color="secondary"
                    onClick={() => handleRemoveEmail(index)}
                    text="Quitar"
                    size="small"
                  />
                )}
              </div>
            ))}

            {errors.email && (
              <Typography color="error" variant="body2">
                {errors.email}
              </Typography>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogFooter>
        {!readOnly && (
          <BotonGenerico
            tipo={"guardar"}
            color="primary"
            onClick={handleSave}
            text={loading ? "Guardando..." : "Guardar"}
          />
        )}
        <BotonGenerico color="primary" onClick={onClose} text={readOnly ? "Cerrar" : "Cancelar"} />
        {/* <Button
            variant="contained"
            color="primary"
            className={classes.actionButton}
            startIcon={<CheckIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Guardando..." : "Guardar"}
          </Button> */}
        {/* <Button
            variant="contained"
            color="primary"
            className={classes.cancelButton}
            startIcon={<CloseIcon />}
            onClick={onClose}
          >
            Cancelar
          </Button> */}
      </DialogFooter>
    </Dialog>
  );
};

export default ABMProveedorModal;
